import classNames from 'classnames';
import React, {MouseEvent} from 'react';

import ErrorTrianglePopover from 'notebook/tables/errors/ErrorTrianglePopover';
import {hasErrorType, LoadingError} from 'notebook/tables/excess/Util';
import {loadingIndicatorAttrs} from 'notebook/util/ExportInstrumentation';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';

import {EvaluatedCellError} from '../excess/Evaluator';

export enum TriangleLocation {
  CELL = 'CELL_LOCATION',
  RUN = 'RUN_LOCATION',
  AGGREGATE = 'AGGREGATE_LOCATION',
  FORM_FIELD = 'FORM_FIELD',
}

export enum TriangleState {
  VALID_NO_TRIANGLE = 'TRISTATE_VALID_NO_TRIANGLE',
  VALID_WITH_TRIANGLE = 'TRISTATE_VALID_WITH_TRIANGLE',
  PARTIALLY_VALID = 'TRISTATE_PARTIALLY_VALID',
  INVALID = 'TRISTATE_INVALID',
  ILLEGAL = 'TRISTATE_ILLEGAL',
  OUTDATED = 'TRISTATE_OUTDATED',
  WARNING = 'TRISTATE_WARNING',
  UNSUBMITTED_CHANGES = 'TRISTATE_UNSUBMITTED_CHANGES',
}

type VisibleTriangleState = Exclude<TriangleState, TriangleState.VALID_NO_TRIANGLE>;

const TRIANGLE_STATUS_CLASS_NAMES: Record<VisibleTriangleState, string> = {
  [TriangleState.VALID_WITH_TRIANGLE]: 'errorTriangle--hasChanges',
  [TriangleState.INVALID]: 'errorTriangle--invalid',
  [TriangleState.PARTIALLY_VALID]: 'errorTriangle--partially-valid',
  [TriangleState.ILLEGAL]: 'errorTriangle--illegal',
  [TriangleState.OUTDATED]: 'errorTriangle--outdated',
  [TriangleState.WARNING]: 'errorTriangle--warning',
  [TriangleState.UNSUBMITTED_CHANGES]: 'errorTriangle--unsubmitted-changes',
};

const LABEL_CLASS_NAMES: Record<VisibleTriangleState, string> = {
  [TriangleState.VALID_WITH_TRIANGLE]: 'label-info',
  [TriangleState.INVALID]: 'label-warning',
  [TriangleState.PARTIALLY_VALID]: 'label-warning',
  [TriangleState.ILLEGAL]: 'label-danger',
  [TriangleState.OUTDATED]: 'label-outdated',
  [TriangleState.WARNING]: 'label-warning',
  [TriangleState.UNSUBMITTED_CHANGES]: 'label-info',
};

const TRIANGLE_LOCATION_CLASS_NAMES: Record<TriangleLocation, string> = {
  [TriangleLocation.CELL]: 'cellValidationIndicator',
  [TriangleLocation.RUN]: 'runValidationIndicator',
  [TriangleLocation.AGGREGATE]: 'aggregateValidationIndicator',
  [TriangleLocation.FORM_FIELD]: 'formFieldValidationIndicator',
};

interface Props {
  dataTestAttrs?: DataTestAttrs;
  location: TriangleLocation;
  error: EvaluatedCellError;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  state: TriangleState;
}

function stopPropagationForMediocreItemSelect(e: MouseEvent<HTMLDivElement>): void {
  e.stopPropagation();
}

/**
 * Component for rendering a triangle in the corner of a table cell (dog ear)
 */
export default function ErrorTriangle({
  dataTestAttrs,
  location,
  error,
  onClick,
  onMouseEnter,
  onMouseLeave,
  state,
}: Props): JSX.Element | null {
  if (state === TriangleState.VALID_NO_TRIANGLE) {
    return null;
  }

  return (
    <ErrorTrianglePopover error={error} labelClass={LABEL_CLASS_NAMES[state]}>
      <div
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onMouseDown={stopPropagationForMediocreItemSelect}
        className={classNames(
          'errorTriangle',
          TRIANGLE_LOCATION_CLASS_NAMES[location],
          TRIANGLE_STATUS_CLASS_NAMES[state]
        )}
        {...getTestProps(dataTestAttrs)}
        {...(hasErrorType(error, LoadingError.type) ? loadingIndicatorAttrs() : {})}
      />
    </ErrorTrianglePopover>
  );
}
